/**
 * Raymarched cumulus shell. Curl-warped fbm shape eroded by billowy Worley
 * detail, lit by a short march toward the sun with a 3-octave multiple
 * scattering approximation (Wrenninge / Guerrilla style) plus a powder term, so
 * edges facing the sun silver-line and the cores go deep blue-grey.
 *
 * Evaluated once into the sky cubemap, not per frame. Owner: lighting.
 */

export const CLOUDS_GLSL = /* glsl */ `
#ifndef AAA_CLOUDS_INCLUDED
#define AAA_CLOUDS_INCLUDED

uniform float uCloudBase;      // metres above sea level
uniform float uCloudTop;
uniform float uCloudCoverage;  // 0 = clear, 1 = overcast
uniform float uCloudDensity;
uniform float uCloudScale;
uniform vec2  uCloudWind;      // metres of offset
uniform float uCloudDetail;
uniform int   uCloudSteps;

/**
 * Every field below is used as a *threshold* on fbm noise, and aaaFbm3's
 * gradients are unnormalised, so its practical output is a narrow band —
 * measured on this build, 3 octaves give 0.50 +/- 0.09, i.e. it never leaves
 * the middle fifth of [0,1]. Thresholding that directly is why uCloudCoverage had
 * no usable range: at 0.58 the whole sky crossed the threshold and the deck
 * came out as a solid featureless slab that self-shadowed to nothing (measured
 * density 1.15 = the saturation value, at every direction in the cube, with the
 * sun contribution marched down to exp(-73)).
 *
 * Expanding by AAA_CLOUD_GAIN spreads it over [0.14, 0.86] so a threshold
 * actually separates cloud from sky.
 */
#define AAA_CLOUD_GAIN 3.6

/** Extinction per metre at unit density. Real cumulus is 0.04-0.1 /m. */
#define AAA_CLOUD_SIGMA 0.05

/**
 * Reflectance of a thick sunlit cumulus. 0.7-0.9 in reality; the deck is the
 * brightest thing in an outdoor frame and this is the number that decides how
 * bright.
 */
#define AAA_CLOUD_ALBEDO 0.88

/**
 * Energy normalisation for the multiple-scattering octave sum below.
 *
 * The octaves are an *approximation*, not an integral, so nothing makes them
 * conserve energy — and measured, they did not. The source term was
 * E * ms * 0.25/PI, which for a thick, fully lit cloud saturates the march at
 * roughly E * 0.09: an effective cloud albedo of 0.28 against the 0.7-0.9 a
 * real cumulus has. Measured consequence on the frame (FINDINGS F13): the sky
 * band of level_square topped out at 2.4 scene units / 13 400 nits, when a
 * sunlit cloud top at golden hour is 25 000-30 000 nits, and no exterior frame
 * in the build had a single pixel above sRGB 0.90 that was not a firefly.
 *
 * The constant is derived, not dialled: the march saturates at the source term
 * of the first optically-thick voxel, where the light march returns tau ~ 1, and
 * at tau = 1 with an isotropic phase the octave sum evaluates to 1.617. So
 *
 *     AAA_CLOUD_MS_NORM = (AAA_CLOUD_ALBEDO / PI) / 1.617
 *
 * makes an optically thick, fully lit, isotropically-viewed cloud emerge at
 * exactly albedo * E / PI, which is the Lambertian answer it should agree
 * with. Every angular dependence — phase, powder, self-shadow — still comes from
 * the march.
 */
#define AAA_CLOUD_MS_TAU1 1.617
#define AAA_CLOUD_MS_NORM ( AAA_CLOUD_ALBEDO / AAA_PI / AAA_CLOUD_MS_TAU1 )

float aaaCloudField( vec3 p, const int octaves ) {
  return clamp( aaaFbm3( p, octaves ) * AAA_CLOUD_GAIN + 0.5, 0.0, 1.0 );
}

float aaaCloudHeightFraction( float h ) {
  return clamp( ( h - uCloudBase ) / max( 1.0, uCloudTop - uCloudBase ), 0.0, 1.0 );
}

/** Vertical profile of a cumulus: flat base, bulging middle, wispy top. */
float aaaCloudProfile( float hf, float cov ) {
  // NB: smoothstep with edge0 > edge1 is undefined in GLSL — it silently
  // returned 0 here on ANGLE/Metal, which zeroed the whole cloud layer.
  float bottom = smoothstep( 0.0, 0.16, hf );
  float top = 1.0 - smoothstep( 0.32 + 0.32 * cov, 1.0, hf );
  return bottom * top;
}

/**
 * Density at a world point. detail switches the expensive erosion pass off
 * for the light march and for the coarse steps, which is most of the samples —
 * a cloud march that evaluates full detail everywhere costs an order of
 * magnitude more for no visible gain.
 */
float aaaCloudDensity( vec3 pWorld, float h, bool detail ) {
  float hf = aaaCloudHeightFraction( h );
  if ( hf <= 0.0 || hf >= 1.0 ) return 0.0;

  // One noise cell is 1 / ( 0.00035 * uCloudScale ) metres — ~800 m at the
  // default scale of 3.6. It used to be 0.00011, i.e. 2.5 km per cell: at
  // zenith the entire visible shell spans barely 1.5 km horizontally, so the
  // whole upper sky sat inside a single cell and had no shape at all.
  vec3 q = vec3( pWorld.x + uCloudWind.x, h, pWorld.z + uCloudWind.y ) * ( 0.00035 * uCloudScale );

  // 1. Large scale weather field decides where clouds may exist at all.
  float weather = aaaCloudField( q * 0.35, 3 );
  float cov = clamp( uCloudCoverage * ( 0.25 + 1.50 * weather ), 0.0, 1.0 );
  if ( cov <= 0.03 ) return 0.0;

  float profile = aaaCloudProfile( hf, cov );
  if ( profile <= 0.001 ) return 0.0;

  // 2. Curl-warped base shape — this is what stops it reading as flat fbm.
  //    Four octaves rather than three: with three, one frequency dominates the
  //    silhouette and the deck reads as a regular lattice of same-sized lumps —
  //    the "mackerel" field the review found across level_rooftop.
  vec3 warp = aaaCurl( q * 1.6 + vec3( 0.0, hf * 1.4, 0.0 ) ) * 0.35;
  float base = aaaCloudField( q * 1.1 + warp, 4 );

  // 3. Coverage is a threshold on the shape field, so it means what it says:
  //    at 0 nothing survives, at 1 everything does, and in between you get
  //    cloud masses with sky between them — which is what the sun march needs
  //    if any edge is ever going to be lit.
  //
  //    The upper edge of the ramp is 1 - 0.28*cov rather than 1 - 0.45*cov:
  //    that is a ~60% wider band on shape, so density climbs from 0 to
  //    saturation over ~60% more distance. It is the only place the silhouette
  //    softness can come from — at sigma 0.05 /m and a step of tens of metres
  //    the transmittance collapses within one sample of any hard boundary,
  //    which is what made the cloud/sky edge a 1-px alpha cutout with visible
  //    staircase at (600,340)->(760,430).
  float shape = base * profile;
  float d = smoothstep( 1.0 - cov, 1.0 - 0.28 * cov, shape );
  if ( d <= 0.001 ) return 0.0;

  // 4. Billowy erosion, stronger at the wispy top and at the silhouette edges.
  //    Two scales, because one gave every billow the same diameter — with the
  //    deck seen edge-on from a rooftop that is exactly a lattice. The erosion
  //    strength is modulated by the weather field too, so some masses stay
  //    smooth-shouldered and others curdle.
  if ( detail ) {
    float w1 = aaaBillow( q + warp * 0.4, 7.5 * uCloudDetail );
    float w2 = aaaBillow( q * 0.34 + 11.7, 7.5 * uCloudDetail );
    float w = mix( w1, w2, 0.38 );
    float erode = mix( 1.0 - w, w, clamp( hf * 1.5, 0.0, 1.0 ) );
    float amount = 0.42 * ( 0.62 + 0.85 * weather );
    d = clamp( ( d - erode * amount * ( 1.0 - d ) - 0.02 ) / 0.72, 0.0, 1.0 );
  }

  return d * uCloudDensity;
}

/**
 * Optical depth toward the sun, cone-widening steps for a soft look.
 *
 * The first step used to be 110 m long and the sample was taken at its far end,
 * so the *surface* of a cloud already accumulated 110 m of its own density —
 * tau >= 5.5 at the default density, i.e. exp(-tau) = 0.004. Nothing was ever
 * lit, which is precisely why the deck read as a flat ambient slab with no sun
 * side and no shadow side. Start at 24 m and sample each segment at its
 * midpoint: a surface voxel now comes out at tau ~ 1, which is what a silver
 * lining is made of.
 */
float aaaCloudLightMarch( vec3 p, float h, vec3 sunDir ) {
  float tau = 0.0;
  float t = 0.0;
  for ( int i = 0; i < 6; i ++ ) {
    float stepLen = 24.0 * pow( 1.85, float( i ) );
    float tm = t + stepLen * 0.5;
    t += stepLen;
    vec3 s = p + sunDir * tm;
    float sh = h + sunDir.y * tm;
    tau += aaaCloudDensity( vec3( s.x, 0.0, s.z ), sh, false ) * stepLen;
  }
  return tau;
}

/**
 * March the shell. Writes accumulated cloud radiance into radiance and
 * returns the remaining transmittance toward the sky behind it.
 */
/**
 * Distance from altitude along a ray with vertical cosine dy to the shell
 * at altitude H.
 *
 * The obvious form — intersect a sphere of radius Rg+H from an origin at Rg+alt
 * — cancels two ~6.4e6 values twice over in fp32 and loses the answer. Here the
 * constant term is factored as (alt-H)(2Rg+alt+H), which is exact, and the root
 * is taken in the citardauq form so there is no cancellation at all.
 */
float aaaShellDistance( float altitude, float dy, float H ) {
  float b = ( AAA_Rg + altitude ) * dy;
  float c = ( altitude - H ) * ( 2.0 * AAA_Rg + altitude + H );   // |p0|^2 - r^2
  float disc = b * b - c;
  if ( disc <= 0.0 ) return -1.0;
  float sq = sqrt( disc );
  // c < 0 when we are below the shell: the positive root is -c / (b + sq).
  float denom = b + sq;
  if ( denom <= 0.0 ) return -1.0;
  return -c / denom;
}

float aaaCloudMarch( vec3 rayDir, vec3 sunDir, vec3 sunIlluminance, vec3 skyAmbient,
                     float altitude, out vec3 radiance ) {
  radiance = vec3( 0.0 );
  if ( rayDir.y < 0.012 ) return 1.0;

  vec3 p0 = vec3( 0.0, AAA_Rg + altitude, 0.0 );
  float tIn  = aaaShellDistance( altitude, rayDir.y, uCloudBase );
  float tOut = aaaShellDistance( altitude, rayDir.y, uCloudTop );
  if ( tIn <= 0.0 || tOut <= tIn ) return 1.0;

  tOut = min( tOut, 150000.0 );
  if ( tOut <= tIn ) return 1.0;

  int steps = uCloudSteps;
  // Cap the step so a grazing ray does not stride kilometres through a field
  // whose finest feature is ~100 m. Near the horizon the march then stops short
  // of tOut, which is fine: the deck is already 80 % blended to sky colour down
  // there by the aerial-perspective term in the bake shader.
  float ds = min( ( tOut - tIn ) / float( steps ), 180.0 );

  float mu = dot( rayDir, sunDir );
  float phaseF = aaaPhaseHG( mu, 0.80 ) * 4.0 * AAA_PI;
  float phaseB = aaaPhaseHG( mu, -0.28 ) * 4.0 * AAA_PI;
  float phase = mix( phaseB, phaseF, 0.75 );   // normalised so isotropic == 1

  float transmittance = 1.0;
  float t = tIn + ds * 0.5;

  for ( int i = 0; i < 160; i ++ ) {
    if ( i >= steps || transmittance < 0.012 || t > tOut ) break;

    /**
     * Stratified offset inside the current segment, indexed by STEP, not by
     * gl_FragCoord.
     *
     * This march is evaluated into a cubemap once, not per frame, so there is
     * nothing downstream to average a per-pixel dither away — no TAA, no
     * accumulation. aaaIGN( gl_FragCoord.xy ) therefore froze its own
     * interleaved-gradient lattice into the cube, and the dome magnifies one
     * cube texel to ~3 screen pixels, which is exactly the coarse checkerboard
     * that covered the whole cloud field. A golden-ratio sequence over the step
     * index is low-discrepancy *within* a pixel and identical *between*
     * pixels: the quadrature error becomes a smooth function of direction
     * instead of noise, so the deck comes out clean at any magnification.
     */
    float jit = fract( 0.5 + float( i ) * 0.6180339887 ) - 0.5;

    vec3 s = p0 + rayDir * ( t + jit * ds );
    float h = length( s ) - AAA_Rg;
    vec3 world = vec3( s.x, 0.0, s.z );

    float d = aaaCloudDensity( world, h, true );
    if ( d > 0.002 ) {
      float sigma = d * AAA_CLOUD_SIGMA;
      float tau = aaaCloudLightMarch( world, h, sunDir ) * AAA_CLOUD_SIGMA;

      /**
       * Multiple scattering, Wrenninge-style: octaves of decreasing extinction,
       * contribution and phase eccentricity. Three octaves with b = 1, 0.3,
       * 0.09 all evaluate to exp(-7) or smaller inside a cumulus core, so the
       * sun term vanished entirely and the deck was lit by skyAmbient alone —
       * identical from every direction, which is what the review reported. Six
       * octaves reach b = 0.005, which is the term that carries the deep,
       * heavily-scattered light real cloud cores glow with.
       */
      float ms = 0.0;
      float a = 1.0, b = 1.0, c = 1.0;
      for ( int o = 0; o < 6; o ++ ) {
        ms += a * exp( -tau * b ) * mix( 1.0, phase, c );
        a *= 0.62; b *= 0.34; c *= 0.55;
      }

      // Powder: darkens the parts of the cloud turned away from the light.
      float powder = 1.0 - exp( -d * 6.0 );
      float lit = ms * mix( 0.55, 1.0, powder );

      // lit carries the phase and the multiple-scattering octaves; the norm
      // above is what ties their sum back to a real cloud albedo. Going through
      // E * phase / 4pi instead (the single-scattering source) understates a
      // thick deck by ~3x, because single scattering is not what makes a cumulus
      // white — see AAA_CLOUD_MS_NORM.
      float hf = aaaCloudHeightFraction( h );
      float ambientOcc = ( 0.16 + 0.84 * hf ) * ( 0.35 + 0.65 * exp( -tau * 0.06 ) );
      vec3 srcCol = sunIlluminance * lit * AAA_CLOUD_MS_NORM + skyAmbient * ambientOcc;

      float stepT = exp( -sigma * ds );
      radiance += srcCol * ( 1.0 - stepT ) * transmittance;
      transmittance *= stepT;
    }

    t += ds * ( d > 0.002 ? 1.0 : 1.6 );
  }

  // Fade the shell out at the horizon so it does not form a hard ring.
  float horizonFade = smoothstep( 0.012, 0.075, rayDir.y );
  radiance *= horizonFade;
  transmittance = mix( 1.0, transmittance, horizonFade );

  return transmittance;
}

#endif
`;
